import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { auditTime, distinctUntilChanged, Subscription } from "rxjs";
import * as _ from "lodash";

import * as AuthActions from "../auth/store/auth.actions";
import * as fromApp from "../store/app.reducer";
import { DataStorageService } from "../services/data-storage/data-storage.service";

@Component({
  selector: "app-upos-login",
  templateUrl: "./upos-login.page.html",
  styleUrls: ["./upos-login.page.scss"],
})
export class UposLoginPage implements OnInit, OnDestroy {
  public mobile = "";
  public password = "";
  public loading = false;
  public loginError: string = null;
  public groupReady = false;

  private groupApp: any;
  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<fromApp.AppState>,
    private router: Router,
    private translateService: TranslateService,
    private dataStorageService: DataStorageService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.store
        .select("groupApp")
        .pipe(distinctUntilChanged())
        .pipe(auditTime(200))
        .subscribe((state) => {
          if (state && state.groupApp && !_.isEqual(this.groupApp, state.groupApp)) {
            this.groupApp = _.cloneDeep(state.groupApp);
          }

          this.groupReady = !!(
            state &&
            state.groupApp &&
            state.groupApp.group
          );
        }),
    );
  }

  ngOnDestroy() {
    _.each(this.subscriptions, (sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  clearLoginError() {
    if (this.loginError) {
      this.loginError = null;
    }
  }

  submitLogin() {
    if (this.loading || !this.groupReady) {
      return;
    }

    const trimmedMobile = this.mobile ? this.mobile.trim() : "";
    const rawPassword = this.password || "";

    this.loginError = null;

    if (!trimmedMobile) {
      this.loginError = this.translateService.instant(
        "popups.magic-login-popup.please-enter-your-mobile",
      );
      return;
    }

    if (!rawPassword.trim()) {
      this.loginError = this.translateService.instant(
        "popups.magic-login-confirmation.please-enter-your-password",
      );
      return;
    }

    this.loading = true;

    this.dataStorageService
      .loginUposCustomer(this.groupApp.group, trimmedMobile, rawPassword)
      .subscribe({
        next: (res: any) => {
          this.loading = false;

          if (res && ((res.success && res.mo_token) || res.mo_token)) {
            this.store.dispatch(new AuthActions.SetLoginState("login_loading"));
            this.persistMoToken(res.mo_token);
            this.password = "";
            this.router.navigateByUrl("/homepage", {
              replaceUrl: true,
            });
            return;
          }

          this.loginError = this.resolveLoginError(res);
        },
        error: (err) => {
          this.loading = false;
          this.loginError = this.resolveLoginError(
            err && err.error ? err.error : err,
          );
        },
      });
  }

  private persistMoToken(moToken) {
    if (moToken && moToken !== "null") {
      if (navigator.cookieEnabled) {
        window.localStorage.setItem("mo_token", moToken);
      }

      this.store.dispatch(new AuthActions.SetMoToken(_.cloneDeep(moToken)));
    }
  }

  private resolveLoginError(response: any) {
    if (response && response.comment_id) {
      const translatedMessage = this.translateService.instant(
        "errorMessages." + response.comment_id,
      );

      if (
        translatedMessage &&
        translatedMessage !== "errorMessages." + response.comment_id
      ) {
        return translatedMessage;
      }
    }

    if (response && response.comments && _.isString(response.comments)) {
      return response.comments;
    }

    if (response && response.message && _.isString(response.message)) {
      return response.message;
    }

    if (response && response.error && _.isString(response.error)) {
      return response.error;
    }

    return this.translateService.instant("errorMessages.classic_error");
  }
}
